import type { ReactNode } from "react"
import { BranchSpendShareWidget } from "@/features/finance/overview/widgets/branch-spend-share-widget"
import { ChurchActivityFinanceWidget } from "@/features/finance/overview/widgets/church-activity-finance-widget"
import { ChurchActivityVolumeWidget } from "@/features/finance/overview/widgets/church-activity-volume-widget"
import { BranchContributionWidget } from "@/features/finance/overview/widgets/branch-contribution-widget"
import { BranchDeficitWidget } from "@/features/finance/overview/widgets/branch-deficit-widget"
import { SilentBranchesWidget } from "@/features/finance/overview/widgets/silent-branches-widget"
import { BranchPerformanceWidget } from "@/features/finance/overview/widgets/branch-performance-widget"
import { FinanceContext, type FinancePageContext } from "@/features/finance/model/finance-tabs"
import { type FinanceSurfaceContext } from "@/features/finance/hooks/finance-context-rules"
import {
  AlertTriangleIcon,
  ArrowLeftRightIcon,
  BuildingIcon,
  HandCoinsIcon,
  HistoryIcon,
  LandmarkIcon,
  PercentIcon,
  PieChartIcon,
  PiggyBankIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TargetIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  WalletIcon,
  ZapIcon,
  type LucideIcon,
} from "lucide-react"

import { FeatureFlag, type FeatureFlagKey } from "@ibada/shared-types/feature-flags"

import type { useTranslations } from "@/i18n"
import {
  DashboardWidgetChrome,
  type DashboardLayout,
  type DashboardWidget,
  type DashboardWidgetRegistry,
  type DashboardWidgetSpan,
} from "@ibada/dashboard-engine"
import {
  ChurchActivitiesWidget,
  ChurchBranchesWidget,
  ChurchCashWidget,
  ChurchRemittancesWidget,
  ChurchShareWidget,
  ChurchSilentWidget,
  ChurchStrugglingWidget,
} from "@/features/finance/overview/church-f3/components/church-f3-widgets"

import {
  BranchAttentionWidget,
  BranchBudgetWidget,
  BranchControlWidget,
  BranchExpensesWidget,
  BranchFundsWidget,
  BranchIncomeWidget,
  BranchMarginWidget,
  BranchPledgesWidget,
  BranchReconciliationWidget,
  BranchRecentWidget,
  BranchTreasuryWidget,
} from "@/features/finance/overview/branch-f1/components/branch-f1-widgets"

import { PledgeSummaryWidget } from "@/features/finance/overview/widgets/pledge-summary-widget"
import { TotalCashWidget } from "@/features/finance/overview/widgets/total-cash-widget"
import { RatioWidget } from "@/features/finance/overview/widgets/ratio-widget"
import { FinanceHealthRatioId } from "@ibada/shared-types/finance"
import { IncomeByAccountWidget } from "@/features/finance/overview/widgets/income-by-account-widget"
import { ExpenseByAccountWidget } from "@/features/finance/overview/widgets/expense-by-account-widget"
import { RecentTransactionsWidget } from "@/features/finance/overview/widgets/recent-transactions-widget"
import { QuickActionsWidget } from "@/features/finance/overview/widgets/quick-actions-widget"
import { MoneyInOutWidget } from "@/features/finance/overview/widgets/money-in-out-widget"
import { BudgetHealthWidget } from "@/features/finance/overview/widgets/budget-health-widget"
import { AccountWatchlistWidget } from "@/features/finance/overview/widgets/account-watchlist-widget"
import { FundWatchlistWidget } from "@/features/finance/overview/widgets/fund-watchlist-widget"
import { NeedsAttentionWidget } from "@/features/finance/overview/widgets/needs-attention-widget"
import { ReconciliationStatusWidget } from "@/features/finance/overview/widgets/reconciliation-status-widget"
import { AuditAssuranceWidget } from "@/features/finance/overview/widgets/audit-assurance-widget"
import { BudgetVsActualWidget } from "@/features/finance/overview/widgets/budget-vs-actual-widget"
import { IncomeBudgetVsActualWidget } from "@/features/finance/overview/widgets/income-budget-vs-actual-widget"
import { FundUtilizationWidget } from "@/features/finance/overview/widgets/fund-utilization-widget"
import { BudgetLinePerformanceWidget } from "@/features/finance/overview/widgets/budget-line-performance-widget"
import { BudgetOverspendWidget } from "@/features/finance/overview/widgets/budget-overspend-widget"
import {
  FINANCE_BRANCH_F1_RATIO_ONLY_SEEDS,
  FINANCE_BRANCH_F1_SPANS,
  FINANCE_BRANCH_F1_WIDGET_SEEDS,
  FINANCE_INSPECTION_F1_RATIO_ONLY_SEEDS,
  FINANCE_INSPECTION_F1_SPANS,
  FINANCE_INSPECTION_F1_WIDGET_SEEDS,
  FINANCE_CHURCH_F3_RATIO_ONLY_SEEDS,
  FINANCE_CHURCH_F3_SPANS,
  FINANCE_CHURCH_F3_WIDGET_SEEDS,
  FINANCE_WIDGET_SEEDS,
  FinanceWidgetId,
} from "@/features/finance/overview/lib/finance-widget-ids"

type Translate = ReturnType<typeof useTranslations>

interface FinanceWidgetDef {
  id: FinanceWidgetId
  /** i18n segment under `finance.overview.widgets.<i18nKey>` (title/subtitle), unless `titleKey` overrides. */
  i18nKey: string
  /** Full i18n path for the title, when it doesn't live at `widgets.<i18nKey>.title` (e.g. ratio widgets). */
  titleKey?: string
  icon: LucideIcon
  hasSubtitle: boolean
  /** Largest span the user may resize this widget to (1 = single column only). */
  maxSpan: DashboardWidgetSpan
  /** Chart/dense widgets open full-size in a dialog; compact KPI/ratio tiles don't. */
  expandable?: boolean
  /**
   * The feature this widget is a window onto. When the flag is off the widget is not in the registry at
   * all — it does not mount, does not query, and does not appear in Add widgets.
   *
   * This is the ONE place a paused feature can be forgotten. A widget for a paused feature that still
   * renders calls an endpoint the server now 403s, so the user sees "Unable to load budget health" — an
   * error card advertising a feature we deliberately hid. Hiding the nav entry and the route is not
   * enough: a dashboard widget is a second door into the same API.
   */
  flag?: FeatureFlagKey
  /**
   * The contexts this widget is meaningful in. OMITTING it means branch and parish only.
   *
   * Deny-by-default is deliberate: GLOBAL consolidates every branch at once, and most widgets are about one
   * branch's own pots — a fund watchlist or a reconciliation queue read as nonsense when silently summed
   * across a workspace. A new widget therefore cannot leak onto the consolidated board by forgetting a field;
   * it has to opt in, having thought about what its numbers mean church-wide.
   */
  contexts?: readonly FinancePageContext[]
  /**
   * Whether this widget puts ANOTHER branch's absolute money on screen (ADR-0024).
   *
   * A second deny-by-default axis, beside `contexts`, and for the same reason: under `BRANCH_AUTONOMY` the
   * parish may see a branch's health as a ratio but not its amounts, and a widget that names branches and
   * their income is the most direct way to break that. Marking the widget is what hides it, so a new
   * cross-branch widget cannot leak by forgetting — it leaks only by claiming, in writing, that it does not.
   *
   * Not the same question as `contexts`. A widget can be perfectly meaningful on the consolidated board and
   * still be exactly what autonomy withholds.
   */
  showsBranchAbsolutes?: boolean
  /** The F3 tiles draw their own card (D4 style); every other widget uses the engine's. */
  chrome?: DashboardWidgetChrome
  /**
   * Every widget receives the board's resolved context (ADR-0024, `useFinanceSurfaceContext`).
   *
   * Optional so a widget that has no opinion about governance — a count, a verdict, an action — can ignore
   * it entirely and keep its zero-argument signature. A widget that DOES state money takes it and stops
   * inferring its situation from whatever it can reach.
   */
  Component: (props: { context?: FinanceSurfaceContext }) => ReactNode
}

/** Branch and parish: one branch each, which is what nearly every widget assumes. */
const DEFAULT_WIDGET_CONTEXTS: readonly FinancePageContext[] = [FinanceContext.Branch, FinanceContext.Parish]

/**
 * Widgets that also read correctly consolidated.
 *
 * Exactly the four served by `GET /finance/overview`, which is the endpoint taught to aggregate over a set of
 * branches. Everything else on this board still resolves ONE branch id — the watchlists, reconciliation,
 * needs-attention, audit assurance and recent transactions would either query a single branch while claiming to
 * cover the church, or fail outright. They join when their own endpoints learn the same trick, not before.
 */
const ALL_CONTEXTS: readonly FinancePageContext[] = [
  FinanceContext.Branch,
  FinanceContext.Parish,
  FinanceContext.Global,
]

/**
 * The widgets that are actually implemented. The registry + initial layout are derived from this list, so
 * un-implemented widgets simply don't render yet — no fake placeholders. `maxSpan` caps how wide each may
 * grow: compact KPIs/ratios stay 1, lists/breakdowns go to 2, and the flow charts can span the full 3.
 */
const GLOBAL_ONLY: readonly FinancePageContext[] = [FinanceContext.Global]
/**
 * The F1 tiles read `GET /finance/overview/branch` and `/home`, which serve one OPERATIONAL branch: the parish keeps
 * its own board, so they are branch-only.
 */
const BRANCH_ONLY: readonly FinancePageContext[] = [FinanceContext.Branch]
const SELF_DRAWN = DashboardWidgetChrome.None

const BUILT_WIDGETS: readonly FinanceWidgetDef[] = [
  // F3, the consolidated board's default tiles. Self-drawn; the money tiles are hidden when absolutes are withheld,
  // while the share and silence tiles state percentages and names only.
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ChurchCash, i18nKey: "churchCash", contexts: GLOBAL_ONLY, icon: WalletIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: ChurchCashWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ChurchBranches, i18nKey: "churchBranches", contexts: GLOBAL_ONLY, icon: BuildingIcon, hasSubtitle: true, maxSpan: 3, chrome: SELF_DRAWN, Component: ChurchBranchesWidget },
  { id: FinanceWidgetId.ChurchShare, i18nKey: "churchShare", contexts: GLOBAL_ONLY, icon: PieChartIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: ChurchShareWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ChurchStruggling, i18nKey: "churchStruggling", contexts: GLOBAL_ONLY, icon: TrendingDownIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: ChurchStrugglingWidget },
  { id: FinanceWidgetId.ChurchSilent, i18nKey: "churchSilent", contexts: GLOBAL_ONLY, icon: AlertTriangleIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: ChurchSilentWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ChurchActivities, i18nKey: "churchActivities", contexts: GLOBAL_ONLY, icon: TargetIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: ChurchActivitiesWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ChurchRemittances, i18nKey: "churchRemittances", contexts: GLOBAL_ONLY, icon: HandCoinsIcon, hasSubtitle: true, maxSpan: 3, chrome: SELF_DRAWN, Component: ChurchRemittancesWidget },
  // F1, the branch board's default tiles. Self-drawn; every one states the branch's amounts, so none survives a
  // withheld scope (that reader keeps the ratio-only board).
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchTreasury, i18nKey: "branchTreasury", contexts: BRANCH_ONLY, icon: WalletIcon, hasSubtitle: true, maxSpan: 3, chrome: SELF_DRAWN, Component: BranchTreasuryWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchAttention, i18nKey: "branchAttention", contexts: BRANCH_ONLY, icon: AlertTriangleIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchAttentionWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchIncome, i18nKey: "branchIncome", contexts: BRANCH_ONLY, icon: TrendingUpIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchIncomeWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchExpenses, i18nKey: "branchExpenses", contexts: BRANCH_ONLY, icon: TrendingDownIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchExpensesWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchMargin, i18nKey: "branchMargin", contexts: BRANCH_ONLY, icon: PercentIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchMarginWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchBudget, i18nKey: "branchBudget", contexts: BRANCH_ONLY, icon: TargetIcon, hasSubtitle: true, maxSpan: 3, chrome: SELF_DRAWN, flag: FeatureFlag.Budgeting, Component: BranchBudgetWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchFunds, i18nKey: "branchFunds", contexts: BRANCH_ONLY, icon: PiggyBankIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchFundsWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchPledges, i18nKey: "branchPledges", contexts: BRANCH_ONLY, icon: HandCoinsIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, flag: FeatureFlag.Pledges, Component: BranchPledgesWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchRecent, i18nKey: "branchRecent", contexts: BRANCH_ONLY, icon: HistoryIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchRecentWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchReconciliation, i18nKey: "branchReconciliation", contexts: BRANCH_ONLY, icon: ScaleIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchReconciliationWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchControl, i18nKey: "branchControl", contexts: BRANCH_ONLY, icon: ShieldCheckIcon, hasSubtitle: true, maxSpan: 2, chrome: SELF_DRAWN, Component: BranchControlWidget },
  // GLOBAL ONLY: at branch or parish scope this is one row restating the cards above it.
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchPerformance, i18nKey: "branchPerformance", contexts: [FinanceContext.Global], icon: BuildingIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: BranchPerformanceWidget },
  // Three more consolidated-only reads of the SAME branch table — the questions a single total cannot answer.
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchContribution, i18nKey: "branchContribution", contexts: [FinanceContext.Global], icon: PieChartIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: BranchContributionWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchDeficit, i18nKey: "branchDeficit", contexts: [FinanceContext.Global], icon: TrendingDownIcon, hasSubtitle: true, maxSpan: 1, expandable: true, Component: BranchDeficitWidget },
  { id: FinanceWidgetId.SilentBranches, i18nKey: "silentBranches", contexts: [FinanceContext.Global], icon: AlertTriangleIcon, hasSubtitle: true, maxSpan: 1, expandable: true, Component: SilentBranchesWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BranchSpendShare, i18nKey: "branchSpendShare", contexts: [FinanceContext.Global], icon: TrendingDownIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: BranchSpendShareWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ChurchActivityFinance, i18nKey: "churchActivityFinance", contexts: [FinanceContext.Global], icon: TargetIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: ChurchActivityFinanceWidget },
  { id: FinanceWidgetId.ChurchActivityVolume, i18nKey: "churchActivityVolume", contexts: [FinanceContext.Global], icon: HistoryIcon, hasSubtitle: true, maxSpan: 1, Component: ChurchActivityVolumeWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.TotalCash, i18nKey: "totalCash", contexts: ALL_CONTEXTS, icon: WalletIcon, hasSubtitle: true, maxSpan: 1, Component: TotalCashWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.MoneyInOut, i18nKey: "moneyInOut", contexts: ALL_CONTEXTS, icon: ArrowLeftRightIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: MoneyInOutWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.AccountWatchlist, i18nKey: "accountWatchlist", icon: LandmarkIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: AccountWatchlistWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.FundWatchlist, i18nKey: "fundWatchlist", icon: PiggyBankIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: FundWatchlistWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BudgetHealth, i18nKey: "budgetHealth", icon: TargetIcon, hasSubtitle: true, maxSpan: 2, expandable: true, flag: FeatureFlag.Budgeting, Component: BudgetHealthWidget },
  // IBA-157 budget performance. Every one carries the Budgeting flag: a widget is a second door into the
  // same API, so hiding the nav entry alone would leave it wide open.
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BudgetVsActual, i18nKey: "budgetVsActual", icon: TargetIcon, hasSubtitle: true, maxSpan: 2, expandable: true, flag: FeatureFlag.Budgeting, Component: BudgetVsActualWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.IncomeBudgetVsActual, i18nKey: "incomeBudgetVsActual", icon: TrendingUpIcon, hasSubtitle: true, maxSpan: 2, expandable: true, flag: FeatureFlag.Budgeting, Component: IncomeBudgetVsActualWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.FundUtilization, i18nKey: "fundUtilization", icon: PiggyBankIcon, hasSubtitle: true, maxSpan: 2, expandable: true, flag: FeatureFlag.Budgeting, Component: FundUtilizationWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BudgetLinePerformance, i18nKey: "budgetLinePerformance", icon: ScaleIcon, hasSubtitle: true, maxSpan: 3, expandable: true, flag: FeatureFlag.Budgeting, Component: BudgetLinePerformanceWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.BudgetOverspend, i18nKey: "budgetOverspend", icon: AlertTriangleIcon, hasSubtitle: true, maxSpan: 2, expandable: true, flag: FeatureFlag.Budgeting, Component: BudgetOverspendWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.PledgeSummary, i18nKey: "pledgeSummary", icon: HandCoinsIcon, hasSubtitle: true, maxSpan: 1, flag: FeatureFlag.Pledges, Component: PledgeSummaryWidget },
  { id: FinanceWidgetId.NeedsAttention, i18nKey: "needsAttention", icon: AlertTriangleIcon, hasSubtitle: false, maxSpan: 2, expandable: true, Component: NeedsAttentionWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ReconciliationStatus, i18nKey: "reconciliationStatus", icon: ScaleIcon, hasSubtitle: false, maxSpan: 2, expandable: true, Component: ReconciliationStatusWidget },
  { id: FinanceWidgetId.AuditAssurance, i18nKey: "auditAssurance", icon: ShieldCheckIcon, hasSubtitle: false, maxSpan: 1, Component: AuditAssuranceWidget },
  { id: FinanceWidgetId.QuickActions, i18nKey: "quickActions", icon: ZapIcon, hasSubtitle: false, maxSpan: 2, Component: QuickActionsWidget },
  // Current/Quick/Debt ratio widgets removed (they need liability data the system does not track). Operating
  // Margin stays — it is computed from the ledger with no liability dependency.
  { id: FinanceWidgetId.OperatingMargin, i18nKey: "financialHealth", titleKey: "widgets.financialHealth.ratios.operatingMargin.name", icon: PercentIcon, hasSubtitle: false, maxSpan: 1, Component: () => <RatioWidget id={FinanceHealthRatioId.OperatingMargin} /> },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.IncomeByAccount, i18nKey: "incomeByAccount", contexts: ALL_CONTEXTS, icon: PieChartIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: IncomeByAccountWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.ExpensesByAccount, i18nKey: "expensesByAccount", contexts: ALL_CONTEXTS, icon: TrendingDownIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: ExpenseByAccountWidget },
  { showsBranchAbsolutes: true, id: FinanceWidgetId.RecentTransactions, i18nKey: "recentTransactions", icon: HistoryIcon, hasSubtitle: true, maxSpan: 2, expandable: true, Component: RecentTransactionsWidget },
]

/**
 * Which feature flags are on, for the widgets that are gated by one. Passed in rather than read here so the
 * registry stays a pure function (it is memoised on `t`, and hooks cannot be called from it).
 */
export type FinanceWidgetFlags = Readonly<Partial<Record<FeatureFlagKey, boolean>>>

/** A widget with no flag is always available; a flagged one only when its flag is on. */
export const isFinanceWidgetAvailable = (
  def: Pick<FinanceWidgetDef, "flag" | "contexts" | "showsBranchAbsolutes">,
  flags: FinanceWidgetFlags,
  context: FinancePageContext,
  /**
   * True when this workspace keeps branch books private (`BRANCH_AUTONOMY`).
   *
   * A default of `false` is safe here and only here: it means "nothing is withheld", which is exactly
   * FULL_PARISH, the mode every workspace is in until someone deliberately chooses otherwise.
   */
  /*
   * The absolutes on this board are withheld from THIS reader (ADR-0024) — not merely "the workspace is on
   * autonomy". The distinction matters: a branch treasurer under autonomy still sees their own branch in
   * full, and only a reader the figures are actually withheld from should lose the money widgets.
   */
  hideBranchAbsolutes = false,
): boolean => {
  if (def.flag !== undefined && flags[def.flag] !== true) return false;
  if (!(def.contexts ?? DEFAULT_WIDGET_CONTEXTS).includes(context)) return false;
  // Hidden rather than emptied: a card headed "Total cash" reading $0.00 is not a smaller truth than $1,200,
  // it is a different and false one — a branch with money and a branch with none look identical.
  if (hideBranchAbsolutes && def.showsBranchAbsolutes) return false;
  return true;
};

const availableWidgets = (
  flags: FinanceWidgetFlags,
  context: FinancePageContext,
  hideBranchAbsolutes: boolean,
): readonly FinanceWidgetDef[] =>
  BUILT_WIDGETS.filter((widget) => isFinanceWidgetAvailable(widget, flags, context, hideBranchAbsolutes))

const BUILT_BY_ID = new Map(BUILT_WIDGETS.map((widget) => [widget.id, widget]))

function resolveTitle(t: Translate, def: FinanceWidgetDef): string {
  return def.titleKey ? t(def.titleKey) : t(`widgets.${def.i18nKey}.title`)
}

/** Build the widget registry (id -> render definition) with translated titles for the current locale. */
export function buildFinanceWidgetRegistry(
  t: Translate,
  flags: FinanceWidgetFlags,
  context: FinancePageContext = FinanceContext.Branch,
  /** Absolutes are withheld from this reader (ADR-0024) — the money widgets are dropped, not emptied. */
  hideBranchAbsolutes = false,
  /**
   * The board's ONE resolved context, handed to every widget it builds.
   *
   * Passed through this closure rather than through the generic dashboard engine on purpose: the engine
   * renders widgets for members, assets and activities too, and none of them have a finance context. The
   * registry is the finance-shaped layer, so it is where the finance-shaped fact belongs.
   */
  surfaceContext?: FinanceSurfaceContext,
): DashboardWidgetRegistry {
  return Object.fromEntries(
    availableWidgets(flags, context, hideBranchAbsolutes).map((widget) => [
      widget.id,
      {
        id: widget.id,
        title: resolveTitle(t, widget),
        subtitle: widget.hasSubtitle ? t(`widgets.${widget.i18nKey}.subtitle`) : undefined,
        icon: <widget.icon className="size-3.5" aria-hidden="true" />,
        maxSpan: widget.maxSpan,
        expandable: widget.expandable,
        chrome: widget.chrome,
        render: () => <widget.Component context={surfaceContext} />,
      },
    ]),
  )
}

/** Build the initial layout (flat order) from the default arrangement, including only implemented widgets. */
export function buildInitialFinanceDashboardLayout(
  t: Translate,
  flags: FinanceWidgetFlags,
  seeds: readonly FinanceWidgetId[] = FINANCE_WIDGET_SEEDS,
  context: FinancePageContext = FinanceContext.Branch,
  /** Absolutes are withheld from this reader (ADR-0024) — seed the health board, not empty money cards. */
  hideBranchAbsolutes = false,
): DashboardLayout {
  const order: string[] = []
  const widgets: Record<string, DashboardWidget> = {}

  for (const id of seeds) {
    const def = BUILT_BY_ID.get(id)
    if (!def || !isFinanceWidgetAvailable(def, flags, context, hideBranchAbsolutes)) continue
    order.push(id)
    widgets[id] = {
      id,
      title: resolveTitle(t, def),
      subtitle: def.hasSubtitle ? t(`widgets.${def.i18nKey}.subtitle`) : undefined,
      icon: <def.icon className="size-3.5" aria-hidden="true" />,
    }
  }

  return { order, widgets, spans: {} }
}

/**
 * The consolidated board's default: exactly F3. The F3 tiles this reader may see are placed in design order with
 * their spans; every other widget available here (the older consolidated widgets) is registered but HIDDEN, so it
 * is offered under "Add widgets" and nothing that existed before is lost.
 *
 * When the absolutes are withheld (ADR-0024) the default is the ratio-only F3 board instead; the money tiles are
 * not available to that reader at all.
 */
export function buildChurchF3DashboardLayout(
  t: Translate,
  flags: FinanceWidgetFlags,
  hideBranchAbsolutes = false,
): DashboardLayout {
  const available = availableWidgets(flags, FinanceContext.Global, hideBranchAbsolutes)
  const availableIds = new Set(available.map((widget) => widget.id))
  const seeds = hideBranchAbsolutes ? FINANCE_CHURCH_F3_RATIO_ONLY_SEEDS : FINANCE_CHURCH_F3_WIDGET_SEEDS
  const order: string[] = seeds.filter((id) => availableIds.has(id))
  const widgets: Record<string, DashboardWidget> = {}
  for (const def of available) {
    widgets[def.id] = {
      id: def.id,
      title: resolveTitle(t, def),
      subtitle: def.hasSubtitle ? t(`widgets.${def.i18nKey}.subtitle`) : undefined,
      icon: <def.icon className="size-3.5" aria-hidden="true" />,
    }
  }
  const spans: Record<string, DashboardWidgetSpan> = {}
  for (const id of order) {
    const span = FINANCE_CHURCH_F3_SPANS[id as FinanceWidgetId]
    if (span) spans[id] = span
  }
  return { order, widgets, spans }
}

/**
 * A self-drawn board's default (F1 on a branch, F1 on Branch Details): the seeded tiles this reader may see in
 * design order with their spans, and every other widget available in the registry handed in HIDDEN, so it is
 * offered under "Add widgets" and nothing that existed before is lost.
 */
function buildSeededLayout(
  t: Translate,
  available: readonly FinanceWidgetDef[],
  seeds: readonly FinanceWidgetId[],
  spanOf: Readonly<Partial<Record<FinanceWidgetId, DashboardWidgetSpan>>>,
): DashboardLayout {
  const availableIds = new Set(available.map((widget) => widget.id))
  const order: string[] = seeds.filter((id) => availableIds.has(id))
  const widgets: Record<string, DashboardWidget> = {}
  for (const def of available) {
    widgets[def.id] = {
      id: def.id,
      title: resolveTitle(t, def),
      subtitle: def.hasSubtitle ? t(`widgets.${def.i18nKey}.subtitle`) : undefined,
      icon: <def.icon className="size-3.5" aria-hidden="true" />,
    }
  }
  const spans: Record<string, DashboardWidgetSpan> = {}
  for (const id of order) {
    const span = spanOf[id as FinanceWidgetId]
    if (span) spans[id] = span
  }
  return { order, widgets, spans }
}

/**
 * The branch board's default: exactly F1, the older branch widgets hidden but addable. When the absolutes are
 * withheld (ADR-0024) the default is the ratio-only board that reader had before; no F1 tile is available to them.
 */
export function buildBranchF1DashboardLayout(
  t: Translate,
  flags: FinanceWidgetFlags,
  hideBranchAbsolutes = false,
): DashboardLayout {
  const available = availableWidgets(flags, FinanceContext.Branch, hideBranchAbsolutes)
  return buildSeededLayout(
    t,
    available,
    hideBranchAbsolutes ? FINANCE_BRANCH_F1_RATIO_ONLY_SEEDS : FINANCE_BRANCH_F1_WIDGET_SEEDS,
    FINANCE_BRANCH_F1_SPANS,
  )
}

/**
 * Branch Details' default (read-only inspection of one branch): the curated F1 tiles, every other widget in the
 * `registry` it is given hidden. Withheld, only the branch's operating margin.
 */
export function buildInspectionF1DashboardLayout(
  t: Translate,
  flags: FinanceWidgetFlags,
  hideBranchAbsolutes = false,
  excluded: readonly FinanceWidgetId[] = [],
): DashboardLayout {
  const available = availableWidgets(flags, FinanceContext.Branch, hideBranchAbsolutes).filter(
    (widget) => !excluded.includes(widget.id),
  )
  return buildSeededLayout(
    t,
    available,
    hideBranchAbsolutes ? FINANCE_INSPECTION_F1_RATIO_ONLY_SEEDS : FINANCE_INSPECTION_F1_WIDGET_SEEDS,
    FINANCE_INSPECTION_F1_SPANS,
  )
}
